import React, { useEffect, useState, useRef, ReactNode } from "react";
import ICONS from "../Constants/Icons.tsx";
import { Image } from "primereact/image";
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom";
import moment from "moment";
import { getLastRefreshDate } from "../Services/dateService.ts";
import { Button } from "primereact/button";

import { updateEngagementTitle } from "../Utils/Common.tsx";
import FooterCard from "../Components/FooterCard.tsx";
import {
  addOrUpdateFavorite,
  deleteFavorite,
  getFavoriteStatus,
} from "../Services/favoriteEngagementService.tsx";
import { FOLLOW_SETTINGS_ERROR_MESSAGE } from "../Constants/Messages.ts";
import useAccessToken from "../Custom/useAccessToken.tsx";
import { Toast } from "primereact/toast";
import { FavoriteEngagement } from "../Models/FavouriteEngagement.ts";
import { useSelector } from "react-redux";
import { RootState } from "../Redux/Store.tsx";
import { useRequestType } from "../Context/RequestTypeContext.tsx";
import { useExport } from "../Context/ExportContext.tsx";
import NotificationBell from "./NotificationBell.tsx";

const DASHBOARD_HEADER_TABS = [
  { label: "Dashboard", value: "dashboard" },
  { label: "Workflow", value: "workflow" },
  { label: "Request", value: "request" },
] as const;

// Workflow/Request dashboard tabs are still in progress; hide them until they're ready for prod.
const SHOW_WORKFLOW_REQUEST_TABS = process.env.REACT_APP_SHOW_WORKFLOW_REQUEST_TABS !== "false";
const VISIBLE_DASHBOARD_HEADER_TABS = SHOW_WORKFLOW_REQUEST_TABS
  ? DASHBOARD_HEADER_TABS
  : DASHBOARD_HEADER_TABS.filter((tab) => tab.value === "dashboard");

const normalizeDashboardSection = (
  value: string | null,
): "dashboard" | "workflow" | "request" => {
  if (value === "workflow" || value === "request") {
    return value;
  }

  return "dashboard";
};

export default function Header() {
  const Icon = ICONS();
  const [engagementTitle, setEngagementTitle] =
    useState<ReactNode>("Dashboard");
  const location = useLocation();
  const navigate = useNavigate();
  const { accessToken } = useAccessToken();
  const toast = useRef<Toast>(null);

  const [engagementData, setEngagementData] = useState<FavoriteEngagement>();
  const { engagementId } = useParams<{ engagementId: string }>();
  const { requestTypeDetails } = useRequestType();
  const [lastUpdatedWIP, setLastUpdatedWIP] = useState(
    localStorage.getItem("lastUpdatedWIP") || "",
  );
  const [loading, setLoading] = useState(false);

  const engagementTask = useSelector(
    (state: RootState) => state.TaskBudget.engagementTask,
  );

  const [searchParams] = useSearchParams();
  const path = location.pathname;
  const isDashboardRoute = path === "/" || path.startsWith("/dashboard");
  const activeDashboardSection = normalizeDashboardSection(
    searchParams.get("section"),
  );

  const showExport = path.includes("/budget/") || path.includes("/monitor/");

  const isEngagementTaskCurrent =
    !!engagementId && engagementTask?.EngagementId === engagementId;

  const handleDashboardTabChange = (
    nextSection: "dashboard" | "workflow" | "request",
  ) => {
    const nextSearchParams = new URLSearchParams(searchParams);

    if (nextSection === "dashboard") {
      nextSearchParams.delete("section");
    } else {
      nextSearchParams.set("section", nextSection);
    }

    const nextSearch = nextSearchParams.toString();
    navigate({
      pathname: location.pathname,
      search: nextSearch ? `?${nextSearch}` : "",
    });
  };

  const renderDashboardTabs = () => (
    <div className="dashboard-header-tabs" role="tablist" aria-label="Dashboard sections">
      {VISIBLE_DASHBOARD_HEADER_TABS.map((tab) => (
        <button
          key={tab.value}
          type="button"
          role="tab"
          aria-selected={activeDashboardSection === tab.value}
          className={`dashboard-header-tab${
            // A single visible tab has nothing to switch between, so skip the active styling.
            VISIBLE_DASHBOARD_HEADER_TABS.length > 1 && activeDashboardSection === tab.value
              ? " is-active"
              : ""
          }`}
          onClick={() => handleDashboardTabChange(tab.value)}
        >          {tab.label}
        </button>
      ))}
    </div>
  );

  useEffect(() => {
    const defaultHeaderTitle = updateEngagementTitle(
      requestTypeDetails,
      isEngagementTaskCurrent ? engagementTask.ClientName : "",
      isEngagementTaskCurrent ? engagementTask.EngagementName : "",
      location.pathname,
    );
    setEngagementTitle(
      isDashboardRoute ? renderDashboardTabs() : defaultHeaderTitle,
    );
  }, [
    activeDashboardSection,
    engagementTask,
    isDashboardRoute,
    isEngagementTaskCurrent,
    location.pathname,
    requestTypeDetails,
  ]);

  useEffect(() => {
    const fetchLastRefreshDate = async () => {
      if (!accessToken || !location.pathname.includes("/monitor")) return;
      try {
        const response = await getLastRefreshDate(accessToken);
        if (response.LastModifiedDate) {
          const utcDate = moment.utc(response.LastModifiedDate);
          setLastUpdatedWIP(utcDate.toISOString());
          localStorage.setItem("lastUpdatedWIP", utcDate.toISOString());
        }
      } catch (error) {
        console.error("Error fetching last refresh date:", error);
      }
    };

    fetchLastRefreshDate();
  }, [accessToken, location.pathname]);

  useEffect(() => {
    const checkFavoriteStatus = async () => {
      if (!accessToken || !engagementId) {
        return;
      }

      try {
        const data = await getFavoriteStatus(engagementId, accessToken);
        setEngagementData(data[0]);
      } catch (err) {
        console.error("Error checking favorite status:", err);
      }
    };

    checkFavoriteStatus();
  }, [accessToken, engagementId, engagementTask, location.pathname]);

  const onStarClick = () => {
    navigate("/");
  };

  const handleToggleFollow = async (engId: string, isFollowed: boolean) => {
    try {
      if (isFollowed) {
        await deleteFavorite(engId, accessToken);
        toast.current?.show({
          severity: "success",
          summary: "Unfollowed",
          detail: "Engagement removed from Dashboard",
          life: 3000,
        });
      } else {
        await addOrUpdateFavorite(engId, accessToken);
        toast.current?.show({
          severity: "success",
          summary: "Following",
          detail: "Engagement added to Dashboard",
          life: 3000,
        });
      }
      const data = await getFavoriteStatus(engagementId!, accessToken);
      setEngagementData(data[0]);
    } catch (err: any) {
      console.error(
        "Error toggling engagement:",
        err.response?.data || err.message,
      );
      toast.current?.show({
        severity: "error",
        summary: "Error",
        detail: FOLLOW_SETTINGS_ERROR_MESSAGE,
        life: 3000,
      });
    }
  };

  const handleCopyLink = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.current?.show({
      severity: "success",
      summary: "Success!",
      detail: "Engagement link copied to clipboard",
      life: 3000,
    });
  };

  const { exportFn } = useExport();

  return (
    <>
      <Toast ref={toast} />
      <header className="header">
        <div className="col flex p-0 align-items-center">
          <Image
            src={Icon.HarmonizeLogo}
            alt="HarmonizeLogo"
            className="lbmc-header-full mt-2 cursor-pointer"
            height="30px"
            onClick={onStarClick}
          />
          <div className="separator ml-3 mt-1"></div>
          <div className="engagement-title ml-3 gap-3 flex align-items-center">
            {engagementTitle}

            {engagementId && (
              <FooterCard
                isFollowed={engagementData?.Disabled === false}
                onToggleFollow={() =>
                  handleToggleFollow(
                    engagementId,
                    engagementData !== undefined
                      ? !engagementData.Disabled
                      : false,
                  )
                }
                link={location.pathname}
                onCopy={handleCopyLink}
              />
            )}
          </div>
        </div>
        <div className="flex align-items-center gap-2 ml-2">
          {(location.pathname.includes("/overview/") ||
            location.pathname.includes("/workflow/") ||
            location.pathname.includes("/budget/") ||
            location.pathname.includes("/monitor/") ||
            location.pathname.includes("/requests/") ||
            location.pathname.includes("/timeentries/")) &&
            lastUpdatedWIP && (
              <span className="last-updated-text text-600 text-sm mr-2">
                <i>Data refreshed {moment(lastUpdatedWIP).fromNow()}</i>
              </span>
            )}
          {showExport && (
            <Button
              label="Export Excel"
              size="small"
              loading={loading}
              disabled={!exportFn}
              onClick={() => exportFn?.()}
            />
          )}
          {!location.pathname.includes("/workflow/") &&
            !location.pathname.includes("/overview/") && (
              <Button
                label="Think"
                size="small"
                onClick={() =>
                  window.dispatchEvent(new CustomEvent("open-think-sidebar"))
                }
              />
            )}
          <NotificationBell />
        </div>
      </header>
    </>
  );
}